// `<SubmitPage>` — top-level route at `/app/submit`.
//
// Composition (UI-SPEC § 4.2.4 wireframe):
//   GlobalNav (sticky)
//   <main id="main">
//     <SubmitForm>             ← single-input glass-strong form (Plan 07-03)
//
// Flow:
//   1. User types address → validation runs in <SubmitForm> on blur.
//   2. Submit fires useSubmitJob mutation (POST /api/jobs).
//   3. On success: navigate('/jobs/<job_id>', { replace: true }) so the
//      back button doesn't return to a blank/submitted form.
//   4. On error: <SubmitForm> renders the inline error panel; user can
//      reset via Retry (mutation.reset()) and resubmit.
//
// The route reads ?address= from the URL search params so the
// `<JobErrorPanel>` "Resubmit this address →" link can prefill the form
// (UI-SPEC § 3.6.3).

import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { GlobalNav } from '@/components/dashboard/GlobalNav';
import { SubmitForm } from '@/components/jobs/SubmitForm';
import { useSubmitJob } from '@/hooks/useSubmitJob';

export function SubmitPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialAddress = searchParams.get('address') || '';
  const mutation = useSubmitJob();

  useEffect(() => {
    const previous = document.title;
    document.title = 'Submit — House Hunter';
    return () => {
      document.title = previous;
    };
  }, []);

  const handleSubmit = (address: string) => {
    mutation.mutate(
      { address },
      {
        onSuccess: (data) => {
          navigate(`/jobs/${data.job_id}`, { replace: true });
        },
      },
    );
  };

  return (
    <>
      <GlobalNav />
      <main
        id="main"
        className="min-h-screen bg-ocean-950 text-foam-50 px-4 pt-[88px] pb-8 md:px-6 md:pb-12"
      >
        <SubmitForm
          initialAddress={initialAddress}
          isPending={mutation.isPending}
          isError={mutation.isError}
          errorMessage={
            mutation.error instanceof Error ? mutation.error.message : undefined
          }
          onSubmit={handleSubmit}
          onRetry={() => mutation.reset()}
        />
      </main>
    </>
  );
}
